import React, { useEffect, useState } from "react";
import { Application, Program } from "../../../core/types/types";
import { useSelector } from "react-redux";
import apiInstance from "../../../utility/apiInstance";

interface PopupProps {
    show: boolean;
    onClose: () => void;
    application: Application | null;
}

const ApplicationDetailsPopup: React.FC<PopupProps> = ({ show, onClose, application }) => {
    const [tasks, setTasks] = useState<any[]>([]);

    useEffect(() => {
        if (show && application) {
            const fetchTasks = async () => {
                try {
                    const response = await apiInstance.get("/api/tasks", {
                        params: {
                            "where[application][equals]": application.id,
                        },
                    });
                    setTasks(response.data.docs || []);
                } catch (error) {
                    console.error("Error fetching application tasks:", error);
                }
            };
            fetchTasks();
        } else {
            setTasks([]);
        }
    }, [show, application]);

    const dateOptions: Intl.DateTimeFormatOptions = {
        day: "2-digit",
        month: "short",
        year: "numeric",
    };

    const updateTaskStatus = async (taskId: string, status: string) => {
        try {
            await apiInstance.patch(`/api/tasks/${taskId}`, { status });
            setTasks((prevTasks) => prevTasks.map(t => t.id === taskId ? { ...t, status } : t));
        } catch (error) {
            console.error("Error updating task status:", error);
        }
    };

    return (
        <div
            className={`modal fade ${show ? "show d-block" : ""}`}
            tabIndex={-1}
            role="dialog"
            style={{ background: show ? "rgba(0,0,0,0.5)" : "transparent" }}
        >
            <div className="modal-dialog-centered w-50 m-auto" role="document">
                <div className="modal-content">
                    {/* Header */}
                    <div className="modal-header">
                        <h5 className="modal-title text-secondary">
                            Program Application
                        </h5>
                        <button
                            type="button"
                            className="btn-close"
                            onClick={onClose}
                        ></button>
                    </div>

                    {/* Body */}
                    <div className="modal-body">
                        <h5 className="fw-bold mb-5 text-center">
                            {application?.program?.programName || ""}
                        </h5>

                        <div className="course-page-content pt-0">
                            <div className="card mb-4 shadow-sm border-0 rounded-3">
                                <div className="card-body">
                                    <div className="row mb-2">
                                        <div className="col-sm-4 fw-semibold text-muted">
                                            Status:
                                        </div>
                                        <div className="col-sm-8">
                                            <div
                                                className={
                                                    application?.status ===
                                                        "pending"
                                                        ? "text-warning fw-bold"
                                                        : application?.status ===
                                                            "in_review"
                                                            ? "text-info fw-bold"
                                                            : application?.status ===
                                                                "approved"
                                                                ? "text-success fw-bold"
                                                                : application?.status ===
                                                                    "declined"
                                                                    ? "text-danger fw-bold"
                                                                    : ""
                                                }
                                            >
                                                {application?.status ||
                                                    "pending"}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="row mb-2">
                                        <div className="col-sm-4 fw-semibold text-muted">
                                            Applicant:
                                        </div>
                                        <div className="col-sm-8">
                                            {application?.student?.firstName ||
                                                ""}{" "}
                                            {application?.student?.lastName ||
                                                ""}
                                        </div>
                                    </div>

                                    <div className="row mb-2">
                                        <div className="col-sm-4 fw-semibold text-muted">
                                            Program:
                                        </div>
                                        <div className="col-sm-8">
                                            {application?.program
                                                ?.programName || ""}
                                        </div>
                                    </div>

                                    <div className="row mb-2">
                                        <div className="col-sm-4 fw-semibold text-muted">
                                            Institution:
                                        </div>
                                        <div className="col-sm-8">
                                            {application?.institution
                                                ?.schoolName || ""}
                                        </div>
                                    </div>

                                    <div className="row mb-2">
                                        <div className="col-sm-4 fw-semibold text-muted">
                                            Application Date:
                                        </div>
                                        <div className="col-sm-8">
                                            {application?.createdAt
                                                ? new Date(
                                                    application?.createdAt
                                                ).toLocaleDateString(
                                                    "en-IN",
                                                    dateOptions
                                                )
                                                : "-"}
                                        </div>
                                    </div>

                                    <div className="row mb-2">
                                        <div className="col-sm-4 fw-semibold text-muted">
                                            {" "}
                                            Program Start Date:
                                        </div>
                                        <div className="col-sm-8">
                                            {application?.program?.start
                                                ? new Date(
                                                    application?.program.start
                                                ).toLocaleDateString(
                                                    "en-IN",
                                                    dateOptions
                                                )
                                                : "-"}
                                        </div>
                                    </div>
                                    <div className="row mb-2">
                                        <div className="col-sm-4 fw-semibold text-muted">
                                            {" "}
                                            Program End Date:
                                        </div>
                                        <div className="col-sm-8">
                                            {application?.program?.end
                                                ? new Date(
                                                    application?.program.end
                                                ).toLocaleDateString(
                                                    "en-IN",
                                                    dateOptions
                                                )
                                                : "-"}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="course-page-content pt-0">
                            <div className="card mb-4 shadow-sm border-0 rounded-3">
                                <div className="card-body">
                                    <h5 className="mb-3 text-primary">
                                        Student Details
                                    </h5>

                                    <div className="row mb-2">
                                        <div className="col-sm-4 fw-semibold text-muted">
                                            Name:
                                        </div>
                                        <div className="col-sm-8">
                                            {application?.student?.firstName ||
                                                ""}{" "}
                                            {application?.student?.lastName ||
                                                ""}
                                        </div>
                                    </div>

                                    <div className="row mb-2">
                                        <div className="col-sm-4 fw-semibold text-muted">
                                            Gender:
                                        </div>
                                        <div className="col-sm-8">
                                            {application?.student?.gender || ""}
                                        </div>
                                    </div>

                                    <div className="row mb-2">
                                        <div className="col-sm-4 fw-semibold text-muted">
                                            First Language:
                                        </div>
                                        <div className="col-sm-8">
                                            {application?.student
                                                ?.firstLanguage || ""}
                                        </div>
                                    </div>

                                    <div className="row mb-2">
                                        <div className="col-sm-4 fw-semibold text-muted">
                                            Country of Citizenship:
                                        </div>
                                        <div className="col-sm-8">
                                            {application?.student
                                                ?.countryOfCitizenship || ""}
                                        </div>
                                    </div>

                                    <div className="row mb-2">
                                        <div className="col-sm-4 fw-semibold text-muted">
                                            Marital Status:
                                        </div>
                                        <div className="col-sm-8">
                                            {application?.student
                                                ?.maritalStatus || ""}
                                        </div>
                                    </div>

                                    <div className="row mb-2">
                                        <div className="col-sm-4 fw-semibold text-muted">
                                            Date of Birth:
                                        </div>
                                        <div className="col-sm-8">
                                            {application?.student?.dob
                                                ? new Date(
                                                    application?.student?.dob
                                                ).toLocaleDateString(
                                                    "en-IN",
                                                    dateOptions
                                                )
                                                : "-"}
                                        </div>
                                    </div>

                                    <div className="row mb-2">
                                        <div className="col-sm-4 fw-semibold text-muted">
                                            Address:
                                        </div>
                                        <div className="col-sm-8">
                                            {[
                                                application?.student
                                                    ?.streetAddress,
                                                application?.student?.city,
                                                application?.student?.state,
                                                application?.student?.country,
                                                application?.student?.zipCode,
                                            ]
                                                .filter(Boolean) // remove empty/undefined values
                                                .join(", ")}
                                        </div>
                                    </div>

                                    <div className="row mb-2">
                                        <div className="col-sm-4 fw-semibold text-muted">
                                            Contact:
                                        </div>
                                        <div className="col-sm-8">
                                            {application?.student?.phone || ""}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="course-page-content pt-0">
                            <div className="card mb-4">
                                <div className="card-body">
                                    <h5 className="mb-3 text-primary">
                                        Program Details
                                    </h5>
                                    {application?.program?.about || ""}
                                </div>
                            </div>
                        </div>
                        {application?.program?.requirement && (
                            <div className="course-page-content pt-0">
                                <div className="card mb-4">
                                    <div className="card-body">
                                        <h5 className="mb-3 text-primary">
                                            Program Requirements
                                        </h5>
                                        {application?.program?.requirement || ""}
                                    </div>
                                </div>
                            </div>
                        )}
                        {tasks && tasks.length > 0 && (
                            <div className="course-page-content pt-0">
                                <div className="card mb-4 shadow-sm border-0 rounded-3">
                                    <div className="card-body">
                                        <h5 className="mb-3 text-primary">
                                            Application Tasks
                                        </h5>
                                        <ul className="list-group list-group-flush">
                                            {tasks.map((task: any) => (
                                                <li key={task.id} className="list-group-item px-0 border-bottom-0 mb-3">
                                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                                        <strong className="text-dark">{task.title}</strong>
                                                        <div className="d-flex align-items-center">
                                                            <span className={`badge rounded-pill ${task.status === 'completed' ? 'bg-success' : (task.status === 'in_progress' || task.status === 'in_review') ? 'bg-info' : 'bg-warning'}`}>
                                                                {task.status ? task.status.replace('_', ' ').toUpperCase() : 'PENDING'}
                                                            </span>
                                                            {task.status !== 'in_review' && task.status !== 'completed' && (
                                                                <button
                                                                    className="btn btn-sm btn-outline-info ms-2 py-0 px-2"
                                                                    style={{ fontSize: '0.75rem' }}
                                                                    onClick={() => updateTaskStatus(task.id, 'in_review')}
                                                                >
                                                                    Mark In Review
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                    {task.description && <p className="mb-0 text-muted small">{task.description}</p>}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ApplicationDetailsPopup;
